import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import filter from "lodash/filter";
import keys from "lodash/keys";
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import reject from "lodash/reject";
import findIndex from "lodash/findIndex";
import DataTable from "components/common/datatable/DataTable";
import TextBox from "components/common/TextBox";
import CheckboxInput from "components/common/CheckboxInput";
import Button from "components/common/Button";
import {PermissionTypes, userHasPermission} from "utils/permissions/rolesPermissions";
import * as UserPermissions from "constants/UserPermissions";
import PlanEnrollmentMapping from "components/admin/elements/PlanEnrollmentMapping";
import * as SERVICE_TYPES from "constants/WellnessPlanServiceTypeIds";

function GreenlineWellnessPlanServices(props) {
    const serviceCategories = [SERVICE_TYPES.GL_SERVICE_ID, SERVICE_TYPES.GL_PRODUCT_ID, SERVICE_TYPES.GL_ADD_ON_ID, SERVICE_TYPES.GL_SPECIAL_ID];

    const handleChange = ({ name, value, enrollmentMapping=null }, row) => {
        const idx = findIndex(props.services, (svc) => row.planServiceTypeId === svc.planServiceTypeId);
        let newData;
        if (!!enrollmentMapping) {
            let mappingKey;
            if (enrollmentMapping.isNew) {
               if (!isNaN(enrollmentMapping.id)) {
                   mappingKey = enrollmentMapping.id;
               } else {
                   mappingKey = Object.keys(props.services[idx].enrollmentMappings).length - 1;
               }
            } else {
                mappingKey = Object.keys(props.services[idx].enrollmentMappings).find(key => props.services[idx].enrollmentMappings[key].id === enrollmentMapping.id);
            }

            newData = {
                ...props.services[idx],
                enrollmentMappings: {
                    ...props.services[idx].enrollmentMappings,
                    [mappingKey]: {
                        ...enrollmentMapping
                    }
                }
            }
        } else {
            newData = {
                ...props.services[idx],
                [name]: value
            }
        }
        props.handleChange({service: newData})
    }

    const AddRow = (row) => {
        const lastMapping = Object.keys(row.enrollmentMappings).length ? orderBy(row.enrollmentMappings, ["id"], ["desc"])[0] : null;
        const nextId = lastMapping ? lastMapping.id + 1 : 0;

        const tempData = {
            ...row.enrollmentMappings,
            [nextId] : {
                id: nextId,
                isNew: true,
                clinicProductId: null
            }
        }
        handleChange({ name: "enrollmentMappings", value: tempData }, row);
    }

    const SERVICE_COLUMNS = [{
        name: "Service",
        selector: "serviceTypeName",
        key: "serviceTypeName",
    }, {
        name: (<div className="text-center">Is Unlimited?</div>),
        selector: "isUnlimited",
        key: "isUnlimited",
        format: row => (
            <div className="flex-centered">
                <CheckboxInput
                    name="isUnlimited"
                    checked={row.isUnlimited}
                    onChange={(data) => handleChange(data, row)}
                    disabled={props.editPlanDisabled}
                />
            </div>
        )
    }, {
        name: "Visits/Uses",
        selector: "maxQuantityAllowedInTerm",
        key: "maxQuantityAllowedInTerm",
        format: row => (
            <TextBox
                name="maxQuantityAllowedInTerm"
                value={row.isUnlimited ? "" : row.maxQuantityAllowedInTerm}
                onChange={(data) => handleChange(data, row)}
                inputType="number"
                min={0}
                disabled={row.isUnlimited || props.editPlanDisabled}
            />
        )
    }];

    const PRODUCTS_COLUMNS = [{
        name: "Product",
        selector: "serviceTypeName",
        key: "serviceTypeName",
    }, {
        name: (<div className="text-center">Is Unlimited?</div>),
        selector: "isUnlimited",
        key: "isUnlimited",
        format: row => (
            <div className="flex-centered">
                <CheckboxInput
                    name="isUnlimited"
                    checked={row.isUnlimited}
                    onChange={(data) => handleChange(data, row)}
                    disabled={props.editPlanDisabled}
                />
            </div>
        )
    }, {
        name: "Months Supply",
        selector: "maxQuantityAllowedInTerm",
        key: "maxQuantityAllowedInTerm",
        format: row => (
            <TextBox
                name="maxQuantityAllowedInTerm"
                value={row.isUnlimited ? null : row.maxQuantityAllowedInTerm}
                onChange={(data) => handleChange(data, row)}
                inputType="number"
                min={0}
                disabled={row.isUnlimited || props.editPlanDisabled}
            />
        )
    }];

    const ADD_ON_COLUMNS = [{
        name: "Add-on",
        selector: "serviceTypeName",
        key: "serviceTypeName",
    }, {
        name: (<div className="text-center">Is Unlimited?</div>),
        selector: "isUnlimited",
        key: "isUnlimited",
        format: row => (
            <div className="flex-centered">
                <CheckboxInput
                    name="isUnlimited"
                    checked={row.isUnlimited}
                    onChange={(data) => handleChange(data, row)}
                    disabled={props.editPlanDisabled}
                />
            </div>
        )
    }, {
        name: (<div className="text-center">Visits/Uses</div>),
        selector: "maxQuantityAllowedInTerm",
        key: "maxQuantityAllowedInTerm",
        format: row => (
            <TextBox
                name="maxQuantityAllowedInTerm"
                value={row.isUnlimited ? "" : row.maxQuantityAllowedInTerm}
                onChange={(data) => handleChange(data, row)}
                inputType="number"
                min={0}
                disabled={row.isUnlimited || props.editPlanDisabled}
            />
        )
    }, {
        name: (
            <div
                className="text-center"
                style={{
                    minWidth: "75px",
                    display: "inline-block",
                }}
            >
                Monthly Cost
            </div>
        ),
        selector: "monthlyAddOnCostToClient",
        key: "monthlyAddOnCostToClient",
        info: "Monthly Add-On cost to Client",
        format: row => (
            <div className="flex flex-end flex-centered">
                <div className="flex-none margin-bottom-x-sm margin-right-x-sm">$</div>
                <div className="flex-1">
                    <TextBox
                        name="monthlyAddOnCostToClient"
                        value={row.monthlyAddOnCostToClient}
                        onChange={(data) => handleChange(data, row)}
                        inputType="number"
                        min={0}
                        disabled={props.editPlanDisabled}
                    />
                </div>
            </div>
        )
    }, {
        name: (
            <div
                className="text-center"
                style={{
                    minWidth: "75px",
                    display: "inline-block",
                }}
            >
                Yearly Cost
            </div>
        ),
        selector: "yearlyAddOnCostToClient",
        key: "yearlyAddOnCostToClient",
        info: "Yearly Add-On cost to Client",
        format: row => (
            <div className="flex flex-end flex-centered">
                <div className="flex-none margin-bottom-x-sm margin-right-x-sm">$</div>
                <div className="flex-1">
                    <TextBox
                        name="yearlyAddOnCostToClient"
                        value={row.yearlyAddOnCostToClient}
                        onChange={(data) => handleChange(data, row)}
                        inputType="number"
                        min={0}
                        disabled={props.editPlanDisabled}
                    />
                </div>
            </div>
        )
    }, {
        name: "Clinic Product that triggers enrollment",
        selector: "enrollmentMappings",
        key: "enrollmentMappings",
        format: row => {
            const enrollmentMappings = reject(row.enrollmentMappings, "isDeleted");
            const hasBlankEnrollmentMapping = !!reject(row.enrollmentMappings, em => !!em.clinicProductId).length;
            return (
                <div className="flex spaced-content">
                    <div className="flex-1">
                        {displayEnrollmentMappings(row)}
                    </div>
                    {props.canEditClinicWellnessPlans &&
                        <Button
                            iconOnly
                            type="success"
                            text
                            onClick={() => AddRow(row)}
                            disabled={hasBlankEnrollmentMapping || enrollmentMappings.length === 0 || props.readOnly}
                        >
                            <i className="fa-2x fa fa-plus-square" />
                        </Button>
                    }
                </div>
            )
        },
    }];

    const getColumns = (category) => {
        switch (category) {
            case SERVICE_TYPES.GL_PRODUCT_ID:
                return PRODUCTS_COLUMNS
            case SERVICE_TYPES.GL_ADD_ON_ID:
                return ADD_ON_COLUMNS;
            case SERVICE_TYPES.GL_SERVICE_ID:
            case SERVICE_TYPES.GL_SPECIAL_ID:
            default:
                return SERVICE_COLUMNS;
        }
    }

    const getServiceTypeName = (serviceTypeId) => {
        switch(serviceTypeId) {
            case SERVICE_TYPES.GL_SERVICE_ID:
                return SERVICE_TYPES.SERVICES
            case SERVICE_TYPES.GL_PRODUCT_ID:
                return SERVICE_TYPES.PRODUCTS;
            case SERVICE_TYPES.GL_ADD_ON_ID:
                return SERVICE_TYPES.ADD_ONS;
            case SERVICE_TYPES.GL_SPECIAL_ID:
                return SERVICE_TYPES.SPECIAL;
            default:
                return "Unknown";
        }
    }

    const handleDeleteMapping = (enrollmentMapping, row) => {
        const servicesKey = Object.keys(props.services).find(key => props.services[key].clinicPlanServiceTypeId === row.clinicPlanServiceTypeId);
        if(enrollmentMapping.isNew) {
            const newEnrollmentMappings = props.services[servicesKey].enrollmentMappings || {};
            delete newEnrollmentMappings[enrollmentMapping.id];
            const newData = {
                ...props.services[servicesKey],
                enrollmentMappings: newEnrollmentMappings
            }
            props.handleChange({service: newData});
        } else {
            const newEnrollmentMapping = {
                ...enrollmentMapping,
                isDeleted: true
            }
            handleChange({enrollmentMapping: newEnrollmentMapping}, row);
        }
    }

    const displayEnrollmentMappings = (row) => {
        //Remove the deleted enrollment mappings
        const enrollmentMappings = reject(row.enrollmentMappings, "isDeleted");
        const lastMapping = enrollmentMappings.length ? orderBy(enrollmentMappings, ["id"], ["desc"])[0] : null;
        const nextId = lastMapping ? lastMapping.id + 1 : 0;

        if (enrollmentMappings.length) {
            return (
                map(enrollmentMappings, (PEM) => (
                    <PlanEnrollmentMapping
                        key={PEM.id}
                        readOnly={props.readOnly}
                        planEnrollmentMapping={PEM}
                        handleChange={(data) => handleChange(data, row)}
                        handleDeleteMapping={() => handleDeleteMapping(PEM, row)}
                    />
                ))
            );
        } else {
            return (
                <PlanEnrollmentMapping
                    planEnrollmentMapping={{}}
                    nextId={nextId}
                    handleChange={(data) => handleChange(data, row)}
                    readOnly={props.readOnly}
                />
            )
        }
    }

    return map(serviceCategories, (category, index) => (
        <div key={index}
            className="margin-bottom-md"
        >
            <DataTable
                title={getServiceTypeName(category)}
                columns={getColumns(category)}
                data={orderBy(filter(props.services, { serviceCategoryId: category }), "planServiceTypeDisplayOrder")}
                pagination={false}
            />
        </div>
    ));
}

GreenlineWellnessPlanServices.defaultPropTypes = {
    readOnly: false
}

GreenlineWellnessPlanServices.propTypes = {
    services: PropTypes.array,
    handleChange: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    editPlanDisabled: PropTypes.bool,
};

export default connect(
    (state) => {
        const userProfile = state.user.userProfile;
        const canEditClinicWellnessPlans = userHasPermission(PermissionTypes.EDIT, UserPermissions.CLINIC_WELLNESS_PLANS, userProfile);
        const canViewProductMapping = userHasPermission(PermissionTypes.VIEW, UserPermissions.PRODUCT_MAPPING, userProfile);
        return {
            canEditClinicWellnessPlans,
            canViewProductMapping,
        }
    }
)(GreenlineWellnessPlanServices);
